// Export helpers: Markdown / CSV builders, a download helper, and the shareable
// progress-card PNG renderer (plain canvas, no dependencies).

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const esc = (s) => String(s ?? "").replace(/\r?\n/g, " ").trim();
const box = (done) => (done ? "[x]" : "[ ]");

/** Human-readable Markdown dump of everything on the page. */
export function buildMarkdown(state) {
  const {
    settings = {},
    completedDays = [],
    partialDays = [],
    tabs = [],
    subGoals = [],
    rememberItems = [],
    clipboardCards = [],
    quickLinkCards = [],
    stickyNotes = [],
    conferenceCards = [],
    publications = [],
    showConferences,
    showPublications,
    stats = {},
  } = state;
  const L = [];
  L.push(`# ${settings.challengeName || "My Challenge"}`, "");
  L.push(
    `Exported ${new Date().toLocaleString()} · Start ${settings.startDate || "?"} · ${
      settings.numberOfDays || "?"
    } days`,
  );
  if (stats.total) {
    L.push(
      `Progress: ${stats.completed}/${stats.total} days (${Math.round(
        (stats.completed / stats.total) * 100,
      )}%) · Streak ${stats.streak} · Best ${stats.bestStreak} · Missed ${stats.missedDays}`,
    );
  }

  L.push("", "## Goals");
  for (const tab of tabs) {
    L.push("", `### ${esc(tab.name)}`);
    for (const g of tab.goals || []) {
      const dl = g.deadline ? ` _(due ${g.deadline})_` : "";
      const imp = g.important ? " ⭐" : "";
      L.push(`- ${box(g.completed)} ${esc(g.text)}${imp}${dl}`);
      for (const sg of subGoals.filter((s) => s.goalId === g.id)) {
        const sdl = sg.deadline ? ` _(due ${sg.deadline})_` : "";
        L.push(`  - ${box(sg.completed)} ${esc(sg.text)}${sdl}`);
      }
    }
  }

  if (rememberItems.length) {
    L.push("", "## Remember");
    for (const r of rememberItems)
      L.push(`- ${box(r.completed)} ${esc(r.text)}${r.dueDate ? ` _(due ${r.dueDate})_` : ""}`);
  }

  if (clipboardCards.length) {
    L.push("", "## Clipboard");
    for (const c of clipboardCards) {
      L.push("", `### ${esc(c.title) || "Untitled"}`);
      const blocks = Array.isArray(c.blocks) && c.blocks.length ? c.blocks : null;
      if (blocks) {
        for (const b of blocks) {
          if (b.type === "todo") L.push(`- ${box(b.checked)} ${esc(b.text)}`);
          else if (b.type === "bullet") L.push(`- ${esc(b.text)}`);
          else if (b.type === "number") L.push(`1. ${esc(b.text)}`);
          else L.push(esc(b.text));
        }
      } else {
        L.push(String(c.content || ""));
      }
    }
  }

  if (quickLinkCards.length) {
    L.push("", "## Quick Links");
    for (const c of quickLinkCards) {
      L.push("", `### ${esc(c.title) || "Links"}${c.dueDate ? ` _(due ${c.dueDate})_` : ""}`);
      for (const l of c.links || [])
        L.push(`- ${box(l.completed)} [${esc(l.description) || l.url}](${l.url})`);
    }
  }

  if (stickyNotes.length) {
    L.push("", "## Sticky Notes");
    for (const n of stickyNotes) {
      const body = String(n.text || "").split("\n").join("\n> ");
      L.push("", `> ${body}${n.dueDate ? `\n> _(due ${n.dueDate})_` : ""}`);
    }
  }

  if (showConferences && conferenceCards.length) {
    L.push("", "## Conferences");
    for (const c of conferenceCards) {
      L.push("", `### ${esc(c.title)}`);
      for (const e of c.entries || []) {
        L.push(
          `- ${e.year || ""}${e.venue ? ` · ${esc(e.venue)}` : ""}${
            e.submissionDeadline ? ` · deadline ${e.submissionDeadline}` : ""
          }${e.paperName ? ` · ${esc(e.paperName)}` : ""} (${e.outcome || "pending"})`,
        );
      }
    }
  }

  if (showPublications && publications.length) {
    L.push("", "## Publications");
    for (const p of publications) {
      const venue =
        p.venueType === "journal"
          ? p.journalName
            ? ` · ${esc(p.journalName)}`
            : ""
          : p.conferenceName
            ? ` · ${esc(p.conferenceName)}`
            : "";
      L.push(`- **${esc(p.title)}** — ${p.status}${venue}`);
    }
  }

  L.push("", "## Calendar");
  L.push(`- Completed: ${completedDays.length ? completedDays.slice().sort().join(", ") : "—"}`);
  L.push(`- Partial: ${partialDays.length ? partialDays.slice().sort().join(", ") : "—"}`);
  return L.join("\n") + "\n";
}

const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Flat CSV of every actionable item (goals, sub-goals, remember, notes, links, days). */
export function buildCsv(state) {
  const rows = [
    ["type", "group", "text", "completed", "deadline", "important", "parent", "created", "updated"],
  ];
  for (const tab of state.tabs || []) {
    for (const g of tab.goals || []) {
      rows.push(["goal", tab.name, g.text, g.completed ? "yes" : "no", g.deadline || "", g.important ? "yes" : "no", "", g.createdAt || "", g.updatedAt || ""]);
      for (const sg of (state.subGoals || []).filter((s) => s.goalId === g.id)) {
        rows.push(["sub-goal", tab.name, sg.text, sg.completed ? "yes" : "no", sg.deadline || "", sg.important ? "yes" : "no", g.text, sg.createdAt || "", sg.updatedAt || ""]);
      }
    }
  }
  for (const r of state.rememberItems || [])
    rows.push(["remember", "", r.text, r.completed ? "yes" : "no", r.dueDate || "", "", "", r.createdAt || "", r.updatedAt || ""]);
  for (const n of state.stickyNotes || [])
    rows.push(["note", n.anchor || "free", n.text, "", n.dueDate || "", "", "", n.createdAt || "", n.updatedAt || ""]);
  for (const c of state.quickLinkCards || [])
    for (const l of c.links || [])
      rows.push(["link", c.title, l.description || l.url, l.completed ? "yes" : "no", c.dueDate || "", "", l.url, c.createdAt || "", c.updatedAt || ""]);
  for (const d of state.completedDays || []) rows.push(["day", "calendar", d, "complete", "", "", "", "", ""]);
  for (const d of state.partialDays || []) rows.push(["day", "calendar", d, "partial", "", "", "", "", ""]);
  return rows.map((r) => r.map(csvCell).join(",")).join("\n") + "\n";
}

// ---------- share card ----------
const hexA = (hex, a) => {
  const h = String(hex).replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return `rgba(255,255,255,${a})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};
const fitText = (ctx, text, maxW) => {
  let t = String(text || "");
  if (ctx.measureText(t).width <= maxW) return t;
  while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
  return t + "…";
};

/**
 * Render a 1200×630 PNG progress card. `days` = [{ status: done|partial|missed|future }].
 * Returns a Blob (or null if canvas is unavailable).
 */
export async function renderShareCard({
  title,
  userName,
  completed,
  total,
  streak,
  bestStreak,
  days = [],
  colors = {},
}) {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const c = {
    bg: colors.bg || "#0b1220",
    a: colors.a || "#facc15",
    b: colors.b || "#22d3ee",
    text: colors.text || "#e2e8f0",
    muted: colors.muted || "#94a3b8",
    future: colors.future || "#2a3752",
    done: "#4ade80",
    partial: "#fbbf24",
    missed: "#f87171",
  };
  try {
    if (document.fonts && document.fonts.load) {
      await Promise.all([
        document.fonts.load('600 64px "Fraunces"'),
        document.fonts.load('500 24px "Inter"'),
      ]);
    }
  } catch (e) {
    /* fall back to system fonts */
  }

  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(150, -50, 0, 150, -50, 700);
  g1.addColorStop(0, hexA(c.b, 0.35));
  g1.addColorStop(1, hexA(c.b, 0));
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, W, H);
  const g2 = ctx.createRadialGradient(1050, 80, 0, 1050, 80, 600);
  g2.addColorStop(0, hexA(c.a, 0.28));
  g2.addColorStop(1, hexA(c.a, 0));
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, W, H);

  ctx.textBaseline = "top";
  ctx.fillStyle = c.text;
  ctx.font = '600 58px "Fraunces", Georgia, serif';
  ctx.fillText(fitText(ctx, title || "My Challenge", W - 160), 80, 70);
  ctx.fillStyle = c.muted;
  ctx.font = '500 24px "Inter", system-ui, sans-serif';
  ctx.fillText(`${userName ? `${userName} · ` : ""}Xunzhine`, 80, 145);

  const pct = total ? Math.round((completed / total) * 100) : 0;
  const grad = ctx.createLinearGradient(80, 0, 480, 0);
  grad.addColorStop(0, c.a);
  grad.addColorStop(1, c.b);
  ctx.fillStyle = grad;
  ctx.font = '600 150px "Fraunces", Georgia, serif';
  ctx.fillText(`${pct}%`, 80, 210);
  ctx.fillStyle = c.text;
  ctx.font = '500 28px "Inter", system-ui, sans-serif';
  ctx.fillText(`${completed} of ${total} days`, 84, 375);

  const stats = [
    ["Streak", String(streak)],
    ["Best", String(bestStreak)],
    ["Done", String(completed)],
  ];
  let sx = 600;
  for (const [label, val] of stats) {
    roundRect(ctx, sx, 230, 170, 120, 20);
    ctx.fillStyle = hexA("#ffffff", 0.07);
    ctx.fill();
    ctx.strokeStyle = hexA("#ffffff", 0.14);
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = c.text;
    ctx.font = '600 44px "Inter", system-ui, sans-serif';
    ctx.fillText(val, sx + 20, 250);
    ctx.fillStyle = c.muted;
    ctx.font = '500 18px "Inter", system-ui, sans-serif';
    ctx.fillText(label.toUpperCase(), sx + 20, 310);
    sx += 190;
  }

  // Day heat-strip, wrapped to fit the remaining height.
  const n = days.length || 1;
  const cols = Math.min(n, 60);
  const rowsN = Math.ceil(n / cols);
  const gap = 6;
  const availH = H - 46 - 20 - 440;
  const size = Math.max(
    6,
    Math.min(22, Math.floor((W - 160 - gap * (cols - 1)) / cols), Math.floor((availH - gap * (rowsN - 1)) / rowsN)),
  );
  days.forEach((d, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = 80 + col * (size + gap);
    const y = 440 + row * (size + gap);
    ctx.fillStyle =
      d.status === "done" ? c.done : d.status === "partial" ? c.partial : d.status === "missed" ? c.missed : c.future;
    roundRect(ctx, x, y, size, size, Math.min(5, size / 3));
    ctx.fill();
  });

  ctx.fillStyle = c.muted;
  ctx.font = '500 18px "Inter", system-ui, sans-serif';
  ctx.fillText(`Made with Xunzhine · ${new Date().toLocaleDateString()}`, 80, H - 46);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}
